import {
  faCircleQuestion,
  faSparkles,
  faTag,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-duotone-svg-icons'

import { ImportedListing } from '../../_types/imported-listing'
import { ListingTag } from '../../_types/listing-tag'
import { QuickPassUnitPreferences } from '../../_types/quickpass-profile'
import { computeMatchScore } from '../match-score-badge/helpers'
import {
  FilterDescriptor,
  ListingsSortOrder,
  ListingsTagFilter,
  SortDescriptor,
} from './types'

const FILTERS: FilterDescriptor[] = [
  { filter: 'all', label: 'All', glyph: faSparkles },
  { filter: 'yes', label: 'Yes', glyph: faThumbsUp },
  { filter: 'maybe', label: 'Maybe', glyph: faCircleQuestion },
  { filter: 'no', label: 'No', glyph: faThumbsDown },
  { filter: 'untagged', label: 'Untagged', glyph: faTag },
]

const SORTS: SortDescriptor[] = [
  { order: 'newest', label: 'Newest first' },
  { order: 'oldest', label: 'Oldest first' },
  { order: 'price-asc', label: 'Price: low to high' },
  { order: 'price-desc', label: 'Price: high to low' },
  { order: 'match-desc', label: 'Best match first' },
]

/**
 * Filters + sorts a listing array based on the renter's toolbar
 * selections. Pure — given the same inputs, returns the same array.
 *
 * @param {ImportedListing[]} listings - the source listings
 * @param {Record<string, ListingTag>} tags - the renter's tag map
 * @param {QuickPassUnitPreferences | null} preferences - for match scoring
 * @param {ListingsTagFilter} tagFilter - the tag filter selection
 * @param {ListingsSortOrder} sortOrder - the sort order selection
 * @returns {ImportedListing[]} the filtered + sorted listings
 */
const applyListingsToolbar = (
  listings: ImportedListing[],
  tags: Record<string, ListingTag>,
  preferences: QuickPassUnitPreferences | null,
  tagFilter: ListingsTagFilter,
  sortOrder: ListingsSortOrder,
): ImportedListing[] => {
  const filtered = listings.filter(listing => {
    const tag = tags[listing.id] ?? null

    if (tagFilter === 'all') {
      return true
    }

    if (tagFilter === 'untagged') {
      return tag === null
    }

    return tag === tagFilter
  })

  const sorted = [...filtered]

  if (sortOrder === 'newest') {
    sorted.sort((alpha, beta) => beta.createdAt - alpha.createdAt)
  } else if (sortOrder === 'oldest') {
    sorted.sort((alpha, beta) => alpha.createdAt - beta.createdAt)
  } else if (sortOrder === 'price-asc') {
    sorted.sort((alpha, beta) => {
      const alphaPrice = alpha.priceMonthly ?? Number.POSITIVE_INFINITY
      const betaPrice = beta.priceMonthly ?? Number.POSITIVE_INFINITY
      return alphaPrice - betaPrice
    })
  } else if (sortOrder === 'price-desc') {
    sorted.sort((alpha, beta) => {
      const alphaPrice = alpha.priceMonthly ?? Number.NEGATIVE_INFINITY
      const betaPrice = beta.priceMonthly ?? Number.NEGATIVE_INFINITY
      return betaPrice - alphaPrice
    })
  } else if (sortOrder === 'match-desc') {
    sorted.sort((alpha, beta) => {
      const alphaScore = computeMatchScore(alpha, preferences).score
      const betaScore = computeMatchScore(beta, preferences).score
      return betaScore - alphaScore
    })
  }

  return sorted
}

export { FILTERS, SORTS, applyListingsToolbar }
